import React from "react";
import { Link } from "react-router-dom";
import {
  Trophy, CheckCircle2, ArrowRight, Brain, Swords,
  HeartPulse, Monitor, Timer, ShieldCheck, Wrench, Ruler, Wind,
} from "lucide-react";
import { Section, Eyebrow, H1, H2, Lead, ButtonPrimary, ButtonGhost, GlassCard } from "../components/ui-kit";
import HealthDashboardMock from "../components/HealthDashboardMock";

const JOURNEY = [
  { month: "M1–M2", title: "Screening & Enrollment", body: "Aptitude, wellness baseline, parental consent, Pro-Path L1 onboarding with curriculum compliance mapping." },
  { month: "M3–M4", title: "Fundamentals", body: "Game-theory, ethics, ergonomics, digital citizenship, English-Hindi fluency drills." },
  { month: "M5–M8", title: "Pro-Path Specialization", body: "Choose vertical: Competitive, Content Creation, Shoutcasting, or Strategy/Analyst." },
  { month: "M9–M10", title: "Scrimmages & Tournaments", body: "Inter-school leagues, VOD reviews, guest sessions from Tier-1 coaches." },
  { month: "M11", title: "Certification", body: "Pro-Path L4 assessment with curriculum compliance mapping, wellness compliance review, Perforange Score issued." },
  { month: "M12", title: "Scouting Portal", body: "Profiles go live to partner eSports orgs, studios, and agencies." },
];

const MODULES = [
  { icon: Swords, title: "Performance", body: "Mechanics, APM, game-sense, clutch-training, match IQ." },
  { icon: Brain, title: "Ethics & Mindset", body: "Sportsmanship, anti-doping, online safety, Bhagavad-Gita-inspired focus protocols." },
  { icon: Trophy, title: "Tactics", body: "IGL calling, meta-analysis, VOD review, competitive psychology." },
];

const SOPS = [
  { icon: Timer, t: "20-20-20 Eye Rule", d: "Every 20 mins, look 20ft away for 20 seconds. Timed by the tracker." },
  { icon: Ruler, t: "Chair & Screen Ergonomics", d: "Monitor at eye-level, lumbar support, 90° elbows. Audited quarterly." },
  { icon: Wind, t: "Breath & Posture Drills", d: "Box-breathing + micro-stretches between scrims." },
  { icon: ShieldCheck, t: "Cognitive Load Caps", d: "Max 90-min sessions, mandated 15-min decompression." },
];

const ERGO = [
  { icon: Monitor, t: "Display & Light Audit", d: "Lux levels, glare, blue-light filters, refresh-rate matching." },
  { icon: Wrench, t: "Chair & Desk Spec", d: "Posture-neutral seating, height-adjustable desks, footrests." },
  { icon: HeartPulse, t: "Wellness Station", d: "Hydration, stretch mats, optional HRV wearables." },
  { icon: ShieldCheck, t: "Safety SOPs", d: "Electrical, fire, screen-time logs, parental dashboards." },
];

export default function Academy() {
  return (
    <div>
      <Section className="!pt-16">
        <Eyebrow>Vertical 01 · Academy & Performance Lab</Eyebrow>
        <H1>The Pro-Path. <br /><span className="text-gradient-orange">Body, mind, and craft</span> — in one program.</H1>
        <Lead className="mt-6">
          A 12-month Pro-Path syllabus with curriculum compliance mapping — paired with an ergonomic, wellness-monitored lab so every school can run a credible esports program from day one.
        </Lead>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/principals-corner" data-testid="academy-cta-empanel">
            <ButtonPrimary>Empanel a School <ArrowRight className="w-4 h-4" /></ButtonPrimary>
          </Link>
          <a href="#performance-lab" data-testid="academy-cta-lab">
            <ButtonGhost>See the Lab <ArrowRight className="w-4 h-4" /></ButtonGhost>
          </a>
        </div>
      </Section>

      {/* Syllabus */}
      <Section className="!pt-0">
        <div className="max-w-3xl mb-10">
          <Eyebrow>12-Month Pro-Path Syllabus</Eyebrow>
          <H2>Performance. Ethics. Tactics.</H2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {MODULES.map((m, i) => {
            const Icon = m.icon;
            return (
              <GlassCard key={i} className="hover:border-[#FF8C00]/40 transition-all" data-testid={`module-${i}`}>
                <div className="w-12 h-12 rounded-xl bg-[#FF8C00]/10 border border-[#FF8C00]/30 flex items-center justify-center">
                  <Icon className="w-6 h-6 text-[#FF8C00]" />
                </div>
                <h3 className="mt-5 font-display text-xl font-black text-white">{m.title}</h3>
                <p className="mt-2 text-sm text-[#A1A1AA]">{m.body}</p>
              </GlassCard>
            );
          })}
        </div>
      </Section>

      {/* Journey infographic */}
      <Section className="!pt-0">
        <div className="max-w-3xl mb-12">
          <Eyebrow color="#00FFD0">Student Journey</Eyebrow>
          <H2>School → Career pathway.</H2>
          <Lead className="mt-4">A verifiable 12-month pipeline, not a promise.</Lead>
        </div>
        <div className="relative" data-testid="journey-infographic">
          <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#FF8C00] via-[#FF8C00]/30 to-[#00FFD0]" />
          <div className="space-y-6">
            {JOURNEY.map((s, i) => (
              <div
                key={i}
                className={`relative flex items-start gap-6 md:grid md:grid-cols-2 md:gap-12 ${
                  i % 2 === 0 ? "" : "md:[&>div:last-child]:order-first"
                }`}
              >
                <div className="absolute left-6 md:left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-[#FF8C00] shadow-[0_0_16px_#FF8C00] mt-6" />
                <div className="hidden md:block" />
                <GlassCard className="ml-14 md:ml-0 flex-1 hover:border-[#FF8C00]/40 transition-all">
                  <p className="text-xs font-mono-tech uppercase tracking-widest text-[#FF8C00]">{s.month}</p>
                  <h3 className="mt-2 font-display font-black text-xl text-white">{s.title}</h3>
                  <p className="mt-2 text-sm text-[#A1A1AA]">{s.body}</p>
                </GlassCard>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section className="!pt-0">
        <div className="max-w-2xl">
          <H2>What each cohort graduates with.</H2>
        </div>
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          {[
            "Perforange Pro-Path completion certificate (with curriculum compliance mapping)",
            "Perforange Score (performance · wellness · ethics)",
            "Wellness clearance modeled on ISO 45001 principles",
            "Digital portfolio on the Scouting Portal",
            "Production House credits for best content",
            "Letters of recommendation for partner organizations",
          ].map((b, i) => (
            <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-white/3 border border-white/5">
              <CheckCircle2 className="w-5 h-5 text-[#00FFD0] shrink-0 mt-0.5" />
              <p className="text-sm text-[#D4D4D8]">{b}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* ============================================================ */}
      {/* PERFORMANCE LAB — anchored sub-page (formerly /performance-lab) */}
      {/* ============================================================ */}
      <div id="performance-lab" className="border-t border-white/8" />

      <Section className="!pt-16">
        <Eyebrow color="#00FFD0">Vertical 01 · Performance Lab</Eyebrow>
        <H1>
          Wellness-tech for the <span className="text-[#00FFD0]">digital athlete</span>.
        </H1>
        <Lead className="mt-6">
          Gaming without wellness is gambling with a generation. The Performance Lab turns every school lab into an ergonomically certified, cognitively monitored training facility.
        </Lead>
      </Section>

      <Section className="!pt-0">
        <HealthDashboardMock />
      </Section>

      {/* Ergo Tech */}
      <Section className="!pt-0">
        <div className="grid md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <Eyebrow>Ergo-Tech Consulting</Eyebrow>
            <H2>Every lab, international-spec.</H2>
            <Lead className="mt-4">
              We audit and certify school labs against WHO display guidelines, ISO 45001 occupational-health principles, and our own Perforange Ergo Index.
            </Lead>
          </div>
          <div className="md:col-span-7 grid sm:grid-cols-2 gap-4">
            {ERGO.map((x, i) => {
              const Icon = x.icon;
              return (
                <GlassCard key={i} className="hover:border-[#00FFD0]/40 transition-all" data-testid={`ergo-${i}`}>
                  <Icon className="w-6 h-6 text-[#00FFD0]" />
                  <h3 className="mt-4 font-display font-black text-lg text-white">{x.t}</h3>
                  <p className="mt-2 text-sm text-[#A1A1AA]">{x.d}</p>
                </GlassCard>
              );
            })}
          </div>
        </div>
      </Section>

      {/* SOPs */}
      <Section className="!pt-0">
        <div className="max-w-3xl mb-10">
          <Eyebrow color="#00FFD0">Wellness SOPs</Eyebrow>
          <H2>Four rituals. Zero excuses.</H2>
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          {SOPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <GlassCard key={i} className="hover:border-[#00FFD0]/40 transition-all" data-testid={`sop-${i}`}>
                <Icon className="w-6 h-6 text-[#00FFD0]" />
                <h3 className="mt-4 font-display font-black text-base text-white">{s.t}</h3>
                <p className="mt-2 text-xs text-[#A1A1AA] leading-relaxed">{s.d}</p>
              </GlassCard>
            );
          })}
        </div>
      </Section>

      <Section className="!pt-0">
        <GlassCard className="md:flex items-center justify-between gap-6">
          <div>
            <H2 className="!text-3xl">Want to see how this works out for your academy?</H2>
            <p className="mt-3 text-[#A1A1AA]">Let&apos;s have a conversation. Tell us about your school and we&apos;ll walk you through what a Perforange rollout could look like.</p>
          </div>
          <Link to="/contact" data-testid="academy-cta-final">
            <ButtonPrimary className="mt-4 md:mt-0">Start a Conversation <ArrowRight className="w-4 h-4" /></ButtonPrimary>
          </Link>
        </GlassCard>
      </Section>
    </div>
  );
}
